import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import type { HelpArticle, NavNode } from "../lib/types";
import { fetchArticleBySlug, fetchRedirect } from "../lib/data";
import { renderMarkdown } from "../lib/markdown";
import { getBreadcrumbs, getPrevNext } from "../lib/navigation";
import { SEO } from "../components/SEO";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableOfContents } from "../components/TableOfContents";
import { PrevNext } from "../components/PrevNext";
import { SITE_ORIGIN } from "../lib/config";

interface HelpPageProps {
  allArticles: HelpArticle[];
  navTree: NavNode[];
}

export function HelpPage({ allArticles, navTree }: HelpPageProps) {
  const params = useParams();
  const navigate = useNavigate();
  const rawSlug = params["*"] || "";
  const slug = "/" + rawSlug;

  const [article, setArticle] = useState<HelpArticle | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const articlesMap = new Map(allArticles.map((a) => [a.slug, a]));

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setError(null);

      const fromCache = articlesMap.get(slug);
      if (fromCache) {
        setArticle(fromCache);
        return;
      }

      try {
        const redirect = await fetchRedirect(slug);
        if (redirect && !cancelled) {
          navigate(redirect, { replace: true });
          return;
        }

        const data = await fetchArticleBySlug(slug);
        if (cancelled) return;
        setArticle(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Unknown error");
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (error) {
    return (
      <div className="error-state">
        <SEO
          title="Error — TrustReq Help Centre"
          description="An error occurred while loading this page."
          canonical={SITE_ORIGIN + slug}
        />
        <p>Something went wrong loading this page. Please try again.</p>
      </div>
    );
  }

  if (article === undefined) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (article === null) {
    return <NotFoundPage slug={slug} />;
  }

  const { html, headings } = renderMarkdown(article.content_markdown);
  const breadcrumbs = getBreadcrumbs(slug, articlesMap);
  const { prev, next } = getPrevNext(slug, allArticles);
  const canonical = SITE_ORIGIN + slug;
  const isHome = article.page_type === "home";

  return (
    <>
      <SEO
        title={`${article.title} — TrustReq Help Centre`}
        description={article.description || article.title}
        canonical={canonical}
        ogType={isHome ? "website" : "article"}
      />
      <div className="article-layout">
        <div className="article-main">
          <Breadcrumbs items={breadcrumbs} />
          <article className="article-content">
            <h1 className="article-title">{article.title}</h1>
            {article.description && (
              <p className="article-description">{article.description}</p>
            )}
            <div
              className="article-body markdown-body"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </article>
          <PrevNext prev={prev} next={next} />
        </div>
        <div className="article-sidebar">
          <TableOfContents headings={headings} />
        </div>
      </div>
    </>
  );
}

function NotFoundPage({ slug }: { slug: string }) {
  return (
    <>
      <SEO
        title="Page not found — TrustReq Help Centre"
        description="The page you are looking for could not be found."
        canonical={SITE_ORIGIN + slug}
      />
      <div className="not-found">
        <h1>404 — Page Not Found</h1>
        <p>The page you are looking for does not exist or has been moved.</p>
        <a href="/" className="not-found-link">← Back to Help Centre</a>
      </div>
    </>
  );
}
